import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FileText,
  CheckCircle2,
  Clock3,
  Wallet,
  ArrowDownToLine,
  AlertCircle,
} from "lucide-react";

interface StatsProps {
  isLoading: boolean;
  stats: {
    // counts
    totalInvoices?: number; // kept for backward compatibility
    totalPaidInvoices?: number;
    totalPendingInvoices?: number;
    // currency strings (already formatted by pages)
    totalValueOfAllInvoices?: string;
    totalPaid?: string; // formatted totalAmountPaid
    pendingAmount?: string; // formatted totalAmountPending
  };
}

export function InvoiceStats({ isLoading, stats }: StatsProps) {
  const cards = [
    {
      title: "Total Invoices",
      value: stats.totalInvoices ?? 0,
      icon: FileText,
      className: "bg-blue-50 text-blue-600",
    },
    {
      title: "Paid Invoices",
      value: stats.totalPaidInvoices ?? 0,
      icon: CheckCircle2,
      className: "bg-emerald-50 text-emerald-600",
    },
    {
      title: "Outstanding Invoice",
      value: stats.totalPendingInvoices ?? 0,
      icon: Clock3,
      className: "bg-amber-50 text-amber-600",
    },
    {
      title: "Total Value",
      value: stats.totalValueOfAllInvoices ?? "0",
      icon: Wallet,
      className: "bg-indigo-50 text-indigo-600",
    },
    {
      title: "Amount Paid",
      value: stats.totalPaid ?? "$0.00",
      icon: ArrowDownToLine,
      className: "bg-green-50 text-green-600",
    },
    {
      title: "Outstanding Amount",
      value: stats.pendingAmount ?? "$0.00",
      icon: AlertCircle,
      className: "bg-rose-50 text-rose-600",
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
            <Skeleton className="h-8 w-1/2 mt-3" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
      {cards.map((card, i) => {
        const Icon = card.icon;
        return (
          <div
            key={i}
            className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">
                {card.title}
              </h3>
              <div className={`p-2 rounded-full ${card.className}`}>
                <Icon className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-2 text-xl font-semibold">{card.value}</p>
          </div>
        );
      })}
    </div>
  );
}
